import React, { createContext, useContext, useEffect, useState } from "react";
import { useLoadScript } from "@react-google-maps/api";
import { GetFavorites } from "../services/PropertyServices";

const FavoritesContext = createContext();
const GoogleMapsContext = createContext();

const libraries = ["places"];

export const FavoritesProvider = ({ children }) => {
  const baseUrl = process.env.REACT_APP_BASE_URL_DEV;
  const [favorites, setFavorites] = useState([]); 
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem("Finder-token");

  // ✅ Load Google Maps script ONCE at the top level
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: "AIzaSyB4D0HM4FufPLxwOFLQbI2-MsHA09yAnAs",
    libraries,
  });

  // Fetch favorites from backend
  const fetchFavorites = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const data = await GetFavorites();
      const favoriteIds = Array.isArray(data.result)
        ? data.result.map((fav) => fav.propertyId)
        : [];
      setFavorites(favoriteIds);
    } catch (error) {
      console.error("Error fetching favorites:", error);
    } finally {
      setLoading(false);
    }
  };

  // Toggle favorite (add/remove)
  const toggleFavorite = async (propertyId) => {
    if (token) {
      try {
        if (favorites.includes(propertyId)) {
          // Remove
          const res = await fetch(`${baseUrl}/api/favorites/${propertyId}`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) throw new Error("Failed to remove favorite");
          setFavorites((prev) => prev.filter((id) => id !== propertyId));
        } else {
          // Add
          const res = await fetch(`${baseUrl}/api/favorites`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ propertyId }),
          });
          if (!res.ok) throw new Error("Failed to add favorite");
          setFavorites((prev) => [...prev, propertyId]);
        }
      } catch (error) {
        console.error("Error toggling favorite:", error);
      }
    } else {
      // Guest user → store in localStorage
      setFavorites((prev) => {
        let updated = [...prev];
        if (updated.includes(propertyId)) {
          updated = updated.filter((id) => id !== propertyId);
        } else {
          updated.push(propertyId);
        }
        localStorage.setItem("likedProperties", JSON.stringify(updated));
        return updated;
      });
    }
  };

  // Sync guest favorites when logged in
  useEffect(() => {
    const syncFavorites = async () => {
      if (token) {
        const localLiked =
          JSON.parse(localStorage.getItem("likedProperties")) || [];
        if (localLiked.length > 0) {
          try {
            await Promise.allSettled(
              localLiked.map((propertyId) =>
                fetch(`${baseUrl}/api/favorites`, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify({ propertyId }),
                })
              )
            );
            localStorage.removeItem("likedProperties");
          } catch (err) {
            console.error("Failed to sync favorites:", err);
          }
        }
        fetchFavorites();
      } else {
        // Guest → load from localStorage
        const localLiked =
          JSON.parse(localStorage.getItem("likedProperties")) || [];
        setFavorites(localLiked);
      }
    };

    syncFavorites();
  }, [token]);

  return (
    <GoogleMapsContext.Provider value={{ isLoaded, loadError }}>
      <FavoritesContext.Provider
        value={{ favorites, toggleFavorite, loading, fetchFavorites }}
      >
        {children}
      </FavoritesContext.Provider>
    </GoogleMapsContext.Provider>
  );
};

export const useFavorites = () => useContext(FavoritesContext);
export const useGoogleMaps = () => useContext(GoogleMapsContext);